import * as amplitude from "@amplitude/analytics-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import { PropsWithChildren, createContext, useEffect, useState } from "react";
import useCurrentUser from "@/hooks/useCurrentUser";
import { getBrowserZoomUserProperties } from "@/lib/browserZoom";
import {
  AMPLITUDE_USER_TYPE_PROPERTY,
  LEGACY_USER_TYPE_PROPERTY,
  resolveAmplitudeUserType
} from "./affiliationIdentify";

export const AmplitudeContext = createContext(amplitude);

const sha256 = async (value: string) => {
  const sourceBytes = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest("SHA-256", sourceBytes);
  const resultBytes = [...new Uint8Array(digest)];
  return resultBytes.map((x) => x.toString(16).padStart(2, "0")).join("");
};

const AmplitudeProvider = ({ children }: PropsWithChildren<{}>) => {
  const apiKey = import.meta.env.VITE_AMPLITUDE_API_KEY;
  const [userIdentifyComplete, setUserIdentifyComplete] = useState(false);
  const { user } = useCurrentUser();

  useEffect(() => {
    if (!apiKey || import.meta.env.MODE === "test") {
      return;
    }
    const sessionReplayTracking = sessionReplayPlugin({
      forceSessionTracking: true,
      sampleRate: 1,
      useWebWorker: true
    });
    amplitude.add(sessionReplayTracking);
    amplitude.init(apiKey, {
      autocapture: {
        attribution: true,
        pageViews: true,
        sessions: true,
        formInteractions: true,
        fileDownloads: true,
        elementInteractions: {
          dataAttributePrefix: "data-amp-track-"
        }
      }
    });
  }, [apiKey]);

  useEffect(() => {
    if (import.meta.env.MODE === "test") return;
    if (userIdentifyComplete) return;

    const identifyAmplitudeUser = async () => {
      const userHash = await sha256(user?.sub ?? "");
      amplitude.setUserId(userHash);

      const accessTokenClaims = await user.accessTokenClaims();

      const identify = new amplitude.Identify();
      identify.set("name", user.name || "");
      identify.set("first_name", user.given_name || "");
      identify.set("last_name", user.family_name || "");
      identify.set("email", user.email || "");
      if (user.department != null) identify.set("department", user.department);
      if (user.location != null) identify.set("location", user.location);
      if (user.organization != null) identify.set("organization", user.organization);
      identify.set("backoffice_sections", accessTokenClaims.backoffice_sections || []);
      identify.set("backoffice_roles", accessTokenClaims.backoffice_roles || []);
      identify.set(AMPLITUDE_USER_TYPE_PROPERTY, resolveAmplitudeUserType(accessTokenClaims.backoffice_roles));
      identify.unset(LEGACY_USER_TYPE_PROPERTY);
      const { browser_zoom_percent, device_pixel_ratio } = getBrowserZoomUserProperties();
      identify.set("browser_zoom_percent", browser_zoom_percent);
      identify.set("device_pixel_ratio", device_pixel_ratio);
      amplitude.identify(identify);

      setUserIdentifyComplete(true);
    };

    identifyAmplitudeUser();
  }, [user, userIdentifyComplete]);

  return <AmplitudeContext.Provider value={amplitude}>{children}</AmplitudeContext.Provider>;
};

export default AmplitudeProvider;
